import { Component, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { User } from './models/User.model';
import { AlertService } from './services/alert/alert.service';
import { HeaderService } from './services/header/header.service';
import { UserService } from './services/log-user/logged-user.service';
import { fecha } from '../environments/fecha';
import { ultimaActualizacion } from '../environments/version';
import { Router, NavigationEnd, NavigationStart, ActivatedRoute } from '@angular/router';
import { from } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { filter, map } from 'rxjs/operators';
import { PedidoService } from './views/pedidos/service/pedido-state.service';
import { SwUpdate } from '@angular/service-worker';
import { AuthService } from './services/http/auth/auth.service';
import { ConfigService } from './services/config/config.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  ultimaActualizacion = ultimaActualizacion;
  fecha = fecha;
  loader: HTMLIonLoadingElement;
  header: boolean;
  user: User;
  pageTitle: string = '';
  customTitle: string = '';
  showMenu: boolean = false;
  showBackButton: boolean = false;
  pedidoButtonText = 'Nuevo Pedido';
  pedidoButtonIcon = 'add-circle';
  hasDraft = false;
  constructor(
    private alerts: AlertService,
    private alertController: AlertController,
    private storage: Storage,
    private headerService: HeaderService,
    private userService: UserService,
    private router: Router,
    private titleService: Title,
    private cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private pedidoService: PedidoService,
    private swUpdate: SwUpdate,
    private authService: AuthService,
    private configService: ConfigService
  ) {}
  async ngOnInit(): Promise<void> {
    this.alerts.message.subscribe(async (alerting) => {
      if (alerting.type) {
        const alert = await this.alertController.create({
          cssClass: 'my-custom-class',
          header: alerting.header,
          message: alerting.message,
          buttons: ['OK'],
        });
        alert.present();
      }
    });
    this.headerService.state.subscribe((state) => (this.header = state));
    this.headerService.customTitleState.subscribe((title) => (this.customTitle = title));
    this.userService.state.subscribe((state) => (this.user = state));
    this.pedidoService.state.subscribe((state) => {
      this.hasDraft = !!state?.cliente;
      if (this.hasDraft) {
        this.pedidoButtonText = state?.id ? `Continuar Pedido Nº${state.id}` : 'Continuar Pedido';
        this.pedidoButtonIcon = 'play';
      } else {
        this.pedidoButtonText = 'Nuevo Pedido';
        this.pedidoButtonIcon = 'add-circle';
      }
    });
    
    // Inicializar título
    this.updatePageTitle();
    
    // Escuchar cambios de ruta
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // Cleanup defensivo ante navegaciones (especialmente en mobile/back button)
        // Evita que un modal custom deje el backdrop/clase modal-open colgado y "pinte" todo negro.
        if (document.body.classList.contains('modal-open')) {
          document.body.style.overflow = '';
          document.body.classList.remove('modal-open');
          const modalElement = document.querySelector('.modal-backdrop') as HTMLElement;
          if (modalElement && modalElement.parentElement) {
            modalElement.parentElement.removeChild(modalElement);
          }
        }

        // Por si quedó un loading colgado en el DOM
        const loadingEl = document.querySelector('ion-loading') as HTMLElement;
        if (loadingEl && loadingEl.parentElement) {
          loadingEl.parentElement.removeChild(loadingEl);
        }
      }
      if (event instanceof NavigationEnd) {
        this.updatePageTitle();
      }
    });
    
    await this.storage.create();
    await this.configService.loadFromStorage();
    from(this.storage.get('user')).subscribe((user) => {
      this.userService.setState(user);
    });
    from(this.storage.get('token')).subscribe((token) => {
      this.userService.setToken(token);
    });

    // Iniciar verificación periódica de token expiración (renovación proactiva)
    this.startTokenRefreshCheck();

    // Verificar actualizaciones del service worker periódicamente (crítico para iOS)
    if (this.swUpdate.isEnabled) {
      // Verificar cada 5 minutos
      setInterval(() => {
        this.swUpdate.checkForUpdate().then(() => {
          }).catch(err => {
          console.error('Service worker update check failed:', err);
        });
      }, 5 * 60 * 1000);

      // Verificar al inicio
      this.swUpdate.checkForUpdate().then(() => {
      }).catch(err => {
        console.error('Initial service worker update check failed:', err);
      });
    }
  }

  async logOut() {
    await this.storage.clear();
    this.router.navigate(['login'], { queryParams: {} }).then((_) => {
      this.userService.setState({} as User);
      this.headerService.setState();
    });
  }

  private startTokenRefreshCheck() {
    // Verificar cada 30 segundos si el token está por expirar o ya expiró
    setInterval(async () => {
      const tokenExpiresAt = await this.storage.get('token_expires_at');
      const rtoExpiresAt = await this.storage.get('rto_expires_at');
      if (!tokenExpiresAt || !rtoExpiresAt) return;

      const now = Date.now();
      const timeUntilExpiry = tokenExpiresAt - now;
      const timeUntilRtoExpiry = rtoExpiresAt - now;
      const tenMinutesInMs = 10 * 60 * 1000;

      // Si el RTO expiró, cerrar sesión
      if (timeUntilRtoExpiry <= 0) {
        await this.logOut();
        return;
      }

      // Renovar si:
      // - Faltan menos de 10 minutos (renovación proactiva normal)
      // - O ya expiró pero el RTO todavía es válido
      if ((timeUntilExpiry > 0 && timeUntilExpiry < tenMinutesInMs) ||
          timeUntilExpiry <= 0) {
        await this.refreshTokenProactively();
      }
    }, 30 * 1000);
  }

  private async refreshTokenProactively() {
    try {
      const rto = await this.storage.get('_token');
      if (!rto) return;

      const res = await this.authService.refreshToken(rto).toPromise();
      const { TOKEN, RTO, USER, TOKEN_exp, RTO_exp } = res;
      const tokenExpiresAt = new Date(TOKEN_exp).getTime();
      const rtoExpiresAt = new Date(RTO_exp).getTime();
      await Promise.all([
        this.storage.set('token', TOKEN),
        this.storage.set('_token', RTO),
        this.storage.set('user', USER),
        this.storage.set('token_expires_at', tokenExpiresAt),
        this.storage.set('rto_expires_at', rtoExpiresAt),
      ]);
      this.userService.setState(USER);
      this.userService.setToken(TOKEN);
    } catch (error) {
      console.error('Error al renovar token proactivamente:', error);
    }
  }

  clientesSaldos() {
    this.router.navigate(['/', 'clientes', 'saldos']);
  }
  
  private updatePageTitle() {
    const url = this.router.url;
    
    // Determinar si mostrar el botón de volver
    this.showBackButton = !url.includes('/home') && !url.includes('/login');
    
    // Obtener el título del data de la ruta si existe
    let routeTitle = '';
    const currentRoute = this.getRouteData();
    if (currentRoute && currentRoute['title']) {
      routeTitle = currentRoute['title'];
    } else {
      // Fallback a detección por URL con nombres específicos según rutas reales
      if (url.includes('/home')) {
        routeTitle = 'Inicio';
      } else if (url.includes('/pedidos/crear')) {
        routeTitle = 'Nuevo Pedido';
      } else if (url.includes('/pedidos/') && url.includes('/ver')) {
        routeTitle = 'Ver Pedido';
      } else if (url.includes('/pedidos') && !url.includes('/crear') && !url.includes('/ver')) {
        routeTitle = 'Mis Pedidos';
      } else if (url.includes('/clientes/') && url.includes('/ver')) {
        routeTitle = 'Viendo Cliente';
      } else if (url.includes('/clientes')) {
        routeTitle = 'Mis Clientes';
      } else if (url.includes('/facturas')) {
        routeTitle = 'Facturas';
      } else if (url.includes('/productos')) {
        routeTitle = 'Productos';
      } else if (url.includes('/archivos')) {
        routeTitle = 'Mis Archivos';
      } else if (url.includes('/login')) {
        routeTitle = 'Login';
      } else {
        routeTitle = 'Inicio';
      }
    }
    
    this.pageTitle = routeTitle;
    this.cdr.detectChanges();
  }
  
  private getRouteData(): any {
    let route = this.activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.data;
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
  }

  navigateToSection(section: string) {
    this.showMenu = false;
    // Limpiar título personalizado si navegamos desde cliente específico
    if (this.router.url.includes('/clientes/') && this.router.url.includes('/ver')) {
      this.headerService.clearCustomTitle();
    }
    this.router.navigate(['/', section], { queryParams: { reset: true } });
  }

  navigateToHome() {
    const url = this.router.url;
    if (url.includes('/clientes/') && url.includes('/ver')) {
      this.headerService.clearCustomTitle();
      this.router.navigate(['/clientes']);
    } else {
      this.router.navigate(['/home']);
    }
  }

  navigateToNewPedido() {
    this.showMenu = false;
    const route = this.pedidoService.getDraftRoute();
    this.router.navigate(route);
  }

  logOutFromMenu() {
    this.showMenu = false;
    this.logOut();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as Element;
    const menuTrigger = document.getElementById('menu-trigger');
    const dropdown = document.querySelector('.menu-dropdown');
    
    // Si el menú está abierto y el clic no es en el trigger ni en el dropdown
    if (this.showMenu && 
        menuTrigger && 
        dropdown && 
        !menuTrigger.contains(target) && 
        !dropdown.contains(target)) {
      this.showMenu = false;
      this.cdr.detectChanges();
    }
  }
}
